import { useEffect } from 'react';
import { useFormDispatch } from '@elfsight/widget-form-builder/widget';
import { getSiteURL } from '@elfsight/widget-utils';
import { useSettings } from '@wu/widget-core';
import { useCurrentTimeZone } from '../current-timezone-provider';
import { useDatetime } from '../datetime/datetime-provider';
import { formatTimeWithTimeZone } from '../datetime/utils/format-time-with-time-zone';
import { useServices } from '../service/services-provider';
import { useDatetimeFormat } from '../utils/use-datetime-format';
import { useRenderPrice } from '../utils/use-render-price';
import {
  BOOKING_END_DATE_ID,
  BOOKING_LOCALIZED_DATETIME_ID,
  BOOKING_PAGE_ID,
  BOOKING_START_DATE_ID,
  BOOKING_TIMEZONE_ID,
  BUSINESS_CONTACTS_ADDRESS_ID,
  BUSINESS_CONTACTS_EMAIL_ID,
  BUSINESS_CONTACTS_PHONE_ID,
  BUSINESS_CONTACTS_WEBSITE_ID,
  BUSINESS_DESCRIPTION_ID,
  BUSINESS_LOGO_ID,
  BUSINESS_NAME_ID,
  OWNER_LANGUAGE_ID,
  SERVICE_DESCRIPTION_ID,
  SERVICE_DURATION_ID,
  SERVICE_NAME_ID,
  SERVICE_PRICE_ID
} from './fields';

export function useBookingHiddenFields() {
  const dispatch = useFormDispatch();
  const {
    businessName,
    businessAbout,
    businessLogo,
    businessContactsAddress,
    businessContactsPhone,
    businessContactsWebsite,
    businessContactsEmail,
    language
  } = useSettings();
  const { timeZone } = useCurrentTimeZone();
  const { currentService } = useServices();
  const { selectedTimeslot } = useDatetime();
  const { formatInterval } = useDatetimeFormat();
  const price = useRenderPrice(currentService?.price);

  const dispatchField = (id: string, value?: string | number) => {
    dispatch({
      type: 'changeField',
      field: {
        id,
        value: value || '',
        valid: true
      }
    });
  };

  useEffect(() => {
    dispatchField(BOOKING_PAGE_ID, getSiteURL('page'));
  }, []);

  useEffect(() => {
    dispatchField(OWNER_LANGUAGE_ID, language?.language);
  }, [language?.language]);

  useEffect(() => {
    dispatchField(BUSINESS_NAME_ID, businessName);
    dispatchField(BUSINESS_DESCRIPTION_ID, businessAbout);
    dispatchField(BUSINESS_LOGO_ID, businessLogo?.url);
    dispatchField(BUSINESS_CONTACTS_ADDRESS_ID, businessContactsAddress);
    dispatchField(BUSINESS_CONTACTS_WEBSITE_ID, businessContactsWebsite);
    dispatchField(BUSINESS_CONTACTS_PHONE_ID, businessContactsPhone);
    dispatchField(BUSINESS_CONTACTS_EMAIL_ID, businessContactsEmail);
  }, [
    businessName,
    businessAbout,
    businessLogo,
    businessContactsEmail,
    businessContactsPhone,
    businessContactsWebsite,
    businessContactsAddress
  ]);

  useEffect(() => {
    if (!currentService) {
      return;
    }

    dispatchField(SERVICE_NAME_ID, currentService.name);
    dispatchField(SERVICE_DESCRIPTION_ID, currentService.description);
    dispatchField(SERVICE_DURATION_ID, currentService.duration);
    dispatchField(SERVICE_PRICE_ID, price);
  }, [currentService, price]);

  useEffect(() => {
    if (!selectedTimeslot) {
      return;
    }

    const { from, to } = selectedTimeslot;

    dispatchField(BOOKING_LOCALIZED_DATETIME_ID, formatInterval(from, to));
    dispatchField(
      BOOKING_START_DATE_ID,
      formatTimeWithTimeZone(from, timeZone)
    );
    dispatchField(BOOKING_END_DATE_ID, formatTimeWithTimeZone(to, timeZone));
    dispatchField(BOOKING_TIMEZONE_ID, timeZone);
  }, [selectedTimeslot, formatInterval, timeZone]);
}
